import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Checkbox } from 'antd';
import { saasOnboardingService } from '../services/api';
import { MIN_PASSWORD_LENGTH, PASSWORD_MIN_LENGTH_MESSAGE, PASSWORD_PLACEHOLDER } from '../utils/passwordPolicy';
import useRuntimeMutation from '../hooks/useRuntimeMutation';
import useRuntimeActionLock from '../hooks/useRuntimeActionLock';
import '../styles/Auth.css';

const SIGNUP_LOCK_KEY = 'signup:submit';

const normalizeSlug = (value) => {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const isValidSlug = (slug) => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug);

const onlyDigits = (value) => String(value || '').replace(/\D/g, '');

const maskCpf = (value) => {
  const digits = onlyDigits(value).slice(0, 11);
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

const maskCnpj = (value) => {
  const digits = onlyDigits(value).slice(0, 14);
  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
};

const maskPhoneBr = (value) => {
  const digits = onlyDigits(value).slice(0, 11);
  if (digits.length <= 2) {
    return digits;
  }
  if (digits.length <= 6) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  }
  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

const getApiDetail = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  if (detail && typeof detail === 'object' && typeof detail.message === 'string') {
    return detail.message;
  }
  return fallback;
};

const SignupPage = () => {
  const navigate = useNavigate();
  const { withActionLock, isLocked } = useRuntimeActionLock();

  const [formData, setFormData] = useState({
    registration_type: '',
    legal_name: '',
    display_name: '',
    document_number: '',
    phone: '',
    slug: '',
    admin_name: '',
    email: '',
    senha: '',
    confirmar_senha: '',
    selected_plan_code: '',
    accepted_terms: false,
  });
  const [availablePlans, setAvailablePlans] = useState([]);
  const [plansUnavailable, setPlansUnavailable] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [partialWarning, setPartialWarning] = useState('');

  React.useEffect(() => {
    let mounted = true;

    const loadPlans = async () => {
      try {
        const response = await saasOnboardingService.listSignupPlans();
        const plans = Array.isArray(response?.data)
          ? response.data
              .map((item) => ({
                code: String(item?.code || '').trim().toUpperCase(),
                name: String(item?.name || '').trim() || String(item?.code || '').trim().toUpperCase(),
                trial_days_default: Number.isFinite(Number(item?.trial_days_default))
                  ? Math.max(0, Number(item.trial_days_default))
                  : 0,
              }))
              .filter((item) => item.code)
          : [];

        if (!mounted) {
          return;
        }

        if (plans.length === 0) {
          setPlansUnavailable(true);
          return;
        }

        setAvailablePlans(plans);
        setPlansUnavailable(false);
        setFormData((prev) => {
          const hasCurrent = plans.some((item) => item.code === prev.selected_plan_code);
          return {
            ...prev,
            selected_plan_code: hasCurrent ? prev.selected_plan_code : plans[0].code,
          };
        });
      } catch {
        if (!mounted) {
          return;
        }
        setPlansUnavailable(true);
      }
    };

    loadPlans();

    return () => {
      mounted = false;
    };
  }, []);

  const onboardingMutation = useRuntimeMutation({
    mutationFn: async (payload) => {
      const response = await saasOnboardingService.signup({
        company_name: payload.companyName,
        slug: payload.slug,
        admin_name: payload.adminName,
        email: payload.email,
        password: payload.password,
        confirm_password: payload.confirmPassword,
        accept_terms: true,
        selected_plan_code: payload.selectedPlanCode,
        customer_type: payload.customerType,
        legal_name: payload.legalName,
        display_name: payload.displayName,
        document_number: payload.documentNumber,
        phone: payload.phone,
      });
      return response.data;
    },
  });

  const loading = onboardingMutation.isPending || isLocked(SIGNUP_LOCK_KEY);

  const resolvedCompanyName = useMemo(() => {
    if (formData.registration_type === 'PF') {
      return String(formData.legal_name || '').trim();
    }
    const fantasia = String(formData.display_name || '').trim();
    if (fantasia) {
      return fantasia;
    }
    return String(formData.legal_name || '').trim();
  }, [formData.registration_type, formData.legal_name, formData.display_name]);

  const slugSuggestion = useMemo(() => normalizeSlug(resolvedCompanyName), [resolvedCompanyName]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const validate = () => {
    if (!formData.registration_type || !formData.slug || !formData.admin_name || !formData.email || !formData.senha || !formData.confirmar_senha) {
      return 'Todos os campos sao obrigatorios.';
    }

    if (!formData.selected_plan_code) {
      return 'Selecione um plano para iniciar seu trial.';
    }

    if (formData.registration_type !== 'PF' && formData.registration_type !== 'PJ') {
      return 'Selecione o tipo de cadastro.';
    }

    if (formData.registration_type === 'PF' && !String(formData.legal_name || '').trim()) {
      return 'Informe o nome completo.';
    }

    if (formData.registration_type === 'PJ') {
      if (!String(formData.legal_name || '').trim()) {
        return 'Informe a razao social.';
      }
      if (!String(formData.display_name || '').trim()) {
        return 'Informe o nome fantasia.';
      }
    }

    const documentDigits = onlyDigits(formData.document_number);
    if (!documentDigits) {
      return formData.registration_type === 'PF' ? 'Informe o CPF.' : 'Informe o CNPJ.';
    }
    if (formData.registration_type === 'PF' && documentDigits.length !== 11) {
      return 'CPF invalido. Verifique os 11 digitos.';
    }
    if (formData.registration_type === 'PJ' && documentDigits.length !== 14) {
      return 'CNPJ invalido. Verifique os 14 digitos.';
    }

    const phoneDigits = onlyDigits(formData.phone);
    if (phoneDigits.length < 10 || phoneDigits.length > 11) {
      return 'Informe um telefone valido com DDD.';
    }

    const normalizedSlug = normalizeSlug(formData.slug);
    if (!isValidSlug(normalizedSlug) || normalizedSlug.length < 3) {
      return 'Slug invalido. Use apenas letras minusculas, numeros e hifen (minimo 3 caracteres).';
    }

    if (formData.senha !== formData.confirmar_senha) {
      return 'As senhas nao coincidem.';
    }

    if (formData.senha.length < MIN_PASSWORD_LENGTH) {
      return PASSWORD_MIN_LENGTH_MESSAGE;
    }

    if (!formData.accepted_terms) {
      return 'Voce precisa aceitar os termos para continuar.';
    }

    if (!resolvedCompanyName) {
      return 'Informe os dados principais do cadastro.';
    }

    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    setPartialWarning('');

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const payload = {
      companyName: resolvedCompanyName,
      slug: normalizeSlug(formData.slug),
      adminName: String(formData.admin_name || '').trim(),
      email: String(formData.email || '').trim().toLowerCase(),
      password: formData.senha,
      confirmPassword: formData.confirmar_senha,
      selectedPlanCode: String(formData.selected_plan_code || '').trim().toUpperCase(),
      customerType: formData.registration_type,
      legalName: String(formData.legal_name || '').trim(),
      displayName:
        formData.registration_type === 'PF'
          ? String(formData.legal_name || '').trim()
          : String(formData.display_name || '').trim(),
      documentNumber: onlyDigits(formData.document_number),
      phone: onlyDigits(formData.phone),
    };

    const run = await withActionLock(SIGNUP_LOCK_KEY, async () => {
      try {
        const result = await onboardingMutation.mutateAsync(payload);

        if (result?.already_processed) {
          setPartialWarning('Onboarding ja havia sido processado anteriormente para este slug/email.');
        }

        setSuccess('Conta SaaS criada com sucesso. Redirecionando para login...');
        setTimeout(() => navigate('/login'), 1800);
      } catch (requestError) {
        const detail = getApiDetail(requestError, 'Nao foi possivel concluir o onboarding.');

        if (String(detail).toLowerCase().includes('slug')) {
          setError('Slug ja esta em uso. Escolha outro slug e tente novamente.');
          return;
        }

        if (String(detail).toLowerCase().includes('email')) {
          setError('Email ja cadastrado. Use outro email para o admin do tenant.');
          return;
        }

        setError(detail);
      }
    });

    if (run?.skipped) {
      setError('Uma solicitacao de onboarding ja esta em andamento. Aguarde a conclusao.');
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card cadastro-card">
        <div className="auth-header">
          <h1>Signup SaaS</h1>
          <p>Crie sua empresa, admin inicial e escolha seu plano para comecar o trial.</p>
        </div>

        <div className="alert alert-success" style={{ marginBottom: 12 }}>
          Nenhuma cobranca sera feita agora. Seu acesso sera iniciado em periodo de teste/analise.
        </div>
        <div className="alert alert-success" style={{ marginBottom: 12 }}>
          A contratacao definitiva sera ativada apos validacao comercial.
        </div>
        {plansUnavailable ? (
          <div className="alert alert-error" style={{ marginBottom: 12 }}>
            Trial temporariamente indisponivel. Entre em contato com o suporte para iniciar seu acesso.
          </div>
        ) : null}

        {error ? <div className="alert alert-error">{error}</div> : null}
        {partialWarning ? <div className="alert alert-error">{partialWarning}</div> : null}
        {success ? <div className="alert alert-success">{success}</div> : null}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label>Tipo de cadastro</label>
            <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="radio"
                  name="registration_type"
                  value="PF"
                  checked={formData.registration_type === 'PF'}
                  onChange={(event) =>
                    setFormData((prev) => ({
                      ...prev,
                      registration_type: event.target.value,
                      document_number: '',
                    }))
                  }
                  disabled={loading}
                />
                Pessoa Fisica
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="radio"
                  name="registration_type"
                  value="PJ"
                  checked={formData.registration_type === 'PJ'}
                  onChange={(event) =>
                    setFormData((prev) => ({
                      ...prev,
                      registration_type: event.target.value,
                      document_number: '',
                    }))
                  }
                  disabled={loading}
                />
                Pessoa Juridica
              </label>
            </div>
          </div>

          {formData.registration_type === 'PF' ? (
            <>
              <div className="form-group">
                <label htmlFor="legal_name">Nome completo</label>
                <input
                  id="legal_name"
                  name="legal_name"
                  type="text"
                  value={formData.legal_name}
                  onChange={handleChange}
                  placeholder="Ex.: Ana Oliveira"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="document_number">CPF</label>
                <input
                  id="document_number"
                  name="document_number"
                  type="text"
                  value={formData.document_number}
                  onChange={(event) =>
                    setFormData((prev) => ({
                      ...prev,
                      document_number: maskCpf(event.target.value),
                    }))
                  }
                  placeholder="000.000.000-00"
                  required
                  disabled={loading}
                />
              </div>
            </>
          ) : null}

          {formData.registration_type === 'PJ' ? (
            <>
              <div className="form-group">
                <label htmlFor="legal_name">Razao social</label>
                <input
                  id="legal_name"
                  name="legal_name"
                  type="text"
                  value={formData.legal_name}
                  onChange={handleChange}
                  placeholder="Ex.: FisiSaaS Servicos LTDA"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="display_name">Nome fantasia</label>
                <input
                  id="display_name"
                  name="display_name"
                  type="text"
                  value={formData.display_name}
                  onChange={handleChange}
                  placeholder="Ex.: FisiSaaS Centro"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="document_number">CNPJ</label>
                <input
                  id="document_number"
                  name="document_number"
                  type="text"
                  value={formData.document_number}
                  onChange={(event) =>
                    setFormData((prev) => ({
                      ...prev,
                      document_number: maskCnpj(event.target.value),
                    }))
                  }
                  placeholder="00.000.000/0000-00"
                  required
                  disabled={loading}
                />
              </div>
            </>
          ) : null}

          <div className="form-group">
            <label htmlFor="phone">Telefone</label>
            <input
              id="phone"
              name="phone"
              type="text"
              value={formData.phone}
              onChange={(event) =>
                setFormData((prev) => ({
                  ...prev,
                  phone: maskPhoneBr(event.target.value),
                }))
              }
              placeholder="(11) 99999-0000"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="slug">Slug</label>
            <input
              id="slug"
              name="slug"
              type="text"
              value={formData.slug}
              onChange={(event) =>
                setFormData((prev) => ({
                  ...prev,
                  slug: normalizeSlug(event.target.value),
                }))
              }
              placeholder={slugSuggestion || 'ex.: fisisaas-clinica-centro'}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="admin_name">Nome do admin</label>
            <input
              id="admin_name"
              name="admin_name"
              type="text"
              value={formData.admin_name}
              onChange={handleChange}
              placeholder="Ex.: Ana Oliveira"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email do admin</label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="admin@empresa.com"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="selected_plan_code">Plano inicial</label>
            {plansUnavailable ? (
              <p style={{ color: '#c0392b', margin: '4px 0 0' }}>
                Nenhum plano de trial disponivel no momento.
              </p>
            ) : availablePlans.length === 0 ? (
              <p style={{ color: '#666', margin: '4px 0 0' }}>Carregando planos...</p>
            ) : (
              <select
                id="selected_plan_code"
                name="selected_plan_code"
                value={formData.selected_plan_code}
                onChange={handleChange}
                required
                disabled={loading}
              >
                {availablePlans.map((plan) => (
                  <option key={plan.code} value={plan.code}>
                    {plan.code} - {plan.name} ({plan.trial_days_default} dias de trial)
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="senha">Senha</label>
            <div className="password-input-group">
              <input
                id="senha"
                name="senha"
                type={showPassword ? 'text' : 'password'}
                value={formData.senha}
                onChange={handleChange}
                placeholder={PASSWORD_PLACEHOLDER}
                required
                disabled={loading}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword((prev) => !prev)}
                disabled={loading}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmar_senha">Confirmar senha</label>
            <input
              id="confirmar_senha"
              name="confirmar_senha"
              type={showPassword ? 'text' : 'password'}
              value={formData.confirmar_senha}
              onChange={handleChange}
              placeholder="Repita a senha"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group" style={{ marginBottom: 4 }}>
            <Checkbox
              checked={formData.accepted_terms}
              onChange={(event) =>
                setFormData((prev) => ({
                  ...prev,
                  accepted_terms: event.target.checked,
                }))
              }
              disabled={loading}
            >
              Aceito os termos basicos de uso da plataforma.
            </Checkbox>
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading || plansUnavailable || availablePlans.length === 0}>
            {loading ? 'Criando ambiente SaaS...' : 'Criar Conta SaaS'}
          </button>
        </form>

        <div className="auth-links">
          <span>Ja possui conta?</span>
          <Link to="/login" className="link">
            Fazer login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
